import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check, Search } from "lucide-react";
import { useDebounceValue } from "usehooks-ts";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetChatContactsInfiniteQuery } from "@/services/(my-space)/api.chat";
import { cn } from "@/utils/cn";
import type { IChatContact } from "@/types/(my-space)/chat.interface";
import { ChatAvatar } from "./ChatAvatar";
import { personName } from "../_lib/chat-labels";

/** Colleague rows, searchable, infinitely scrolled. Shared by every people picker. */
export const ContactList = ({
  excludeIds = [],
  selectedIds,
  onPick,
  header,
  className,
}: {
  excludeIds?: number[];
  selectedIds?: Set<number>;
  onPick: (contact: IChatContact) => void;
  /**
   * Rendered between the search field and the list, scrolling with the list.
   * A function gets the search as typed, to filter rows of its own.
   */
  header?: ReactNode | ((search: string) => ReactNode);
  className?: string;
}) => {
  const { t } = useTranslation("chat");
  const [search, setSearch] = useState("");
  const [debounced] = useDebounceValue(search, 300);
  const query = useGetChatContactsInfiniteQuery({ search: debounced });
  const exclude = useMemo(() => new Set(excludeIds), [excludeIds]);
  const contacts = useMemo(
    () => (query.data?.pages.flatMap((p) => p.data) ?? []).filter((c) => !exclude.has(c.id)),
    [exclude, query.data],
  );

  const sentinelRef = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <div className="relative shrink-0 pb-3">
        <Search className="pointer-events-none absolute start-3 top-[calc(50%-6px)] size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("new.search_people")}
          aria-label={t("new.search_people")}
          className="h-10 ps-9"
          autoFocus
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {typeof header === "function" ? header(search) : header}
        {query.isLoading ? (
          <div className="flex flex-col gap-2 py-1">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="flex items-center gap-3 px-2 py-1.5">
                <Skeleton className="size-9 rounded-full" />
                <Skeleton className="h-3.5 w-1/2" />
              </div>
            ))}
          </div>
        ) : contacts.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t("new.no_contacts")}</p>
        ) : (
          <ul className="flex flex-col">
            {contacts.map((c) => {
              const selected = selectedIds?.has(c.id) ?? false;
              const subtitle = [c.position, c.department].filter(Boolean).join(" · ");
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => onPick(c)}
                    aria-pressed={selectedIds ? selected : undefined}
                    className={cn(
                      "flex w-full items-center gap-4 rounded-md px-3 py-2.5 text-start hover:bg-muted",
                      selected && "bg-primary/5",
                    )}
                  >
                    <ChatAvatar name={personName(t, c)} imageUrl={c.profileImage} size="md" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium text-foreground">
                        {personName(t, c)}
                      </span>
                      {subtitle ? (
                        <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
                      ) : null}
                    </div>
                    {selectedIds ? (
                      <span
                        className={cn(
                          "flex size-5 shrink-0 items-center justify-center rounded-full border",
                          selected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border-strong",
                        )}
                      >
                        {selected ? <Check className="size-3" /> : null}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
            <div ref={sentinelRef} className="h-1" />
          </ul>
        )}
      </div>
    </div>
  );
};
